import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import Container from "@/components/Shared/Container";
import { navData } from "@/components/Navigation/navData";

interface ServicesMegaMenuProps {
  language: "en" | "es";
}

export const ServicesMegaMenu: React.FC<ServicesMegaMenuProps> = ({ language }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const rootRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const services = navData[language].services;
  // The page itself, ignoring the trailing slash live URLs carry; marked for screen readers too.
  const here = location.pathname.replace(/\/+$/, "") || "/";
  const isActive = services.activePrefixes.some((prefix) => here === prefix || here.startsWith(`${prefix}/`));
  const currentPage = (to: string) => (here === (to.replace(/\/+$/, "") || "/") ? "page" : undefined);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Every close path must go through here — a pending open timer left behind
  // would reopen the panel right after a link click navigates.
  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      setOpen(true);
    }, 80);
  };

  const handlePointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    // 300ms bridges the trigger→panel hover gap without feeling sticky.
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 300);
  };

  const handleClick = () => {
    clearTimers();
    setOpen((prev) => !prev);
  };

  const focusFirstOnOpenRef = useRef(false);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      clearTimers();
      focusFirstOnOpenRef.current = true;
      setOpen(true);
    }
  };

  // Focus the first link after the open render commits — a timer here races
  // the AnimatePresence mount and intermittently loses.
  useEffect(() => {
    if (!open || !focusFirstOnOpenRef.current) return;
    focusFirstOnOpenRef.current = false;
    const firstLink = document.querySelector<HTMLAnchorElement>(
      "#services-mega-panel a[href]"
    );
    firstLink?.focus();
  }, [open]);

  const handleBlurCapture = (e: React.FocusEvent<HTMLLIElement>) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) {
      closeNow();
    }
  };

  useEffect(() => {
    closeNow();
  }, [location.pathname, closeNow]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeNow();
        triggerRef.current?.focus();
      }
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeNow();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, closeNow]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return (
    <li ref={rootRef} className="relative" onBlurCapture={handleBlurCapture}>
      <button
        ref={triggerRef}
        type="button"
        className={`nav-link whitespace-nowrap gap-1.5 ${isActive ? "border-accent text-accent-text" : ""}`}
        onClick={handleClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={open}
        aria-controls="services-mega-panel"
        aria-current={isActive ? "true" : undefined}
      >
        {services.trigger}
        <m.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </m.span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <m.div
              className="fixed inset-x-0 top-[5.3125rem] h-screen scrim hidden xl:block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0.1 : 0.2 }}
              aria-hidden="true"
              onClick={closeNow}
              onPointerEnter={(e) => {
                if (e.pointerType !== "mouse") return;
                clearTimers();
                closeTimerRef.current = setTimeout(() => setOpen(false), 100);
              }}
            />

            <m.div
              id="services-mega-panel"
              aria-label={services.panelLabel}
              onPointerEnter={handlePointerEnter}
              onPointerLeave={handlePointerLeave}
              className="fixed inset-x-0 top-[5.3125rem] hidden xl:block bg-surface-raised border-b border-border shadow-2 max-h-[calc(100vh-7.5rem)] overflow-y-auto"
              initial={{ opacity: 0, y: reduceMotion ? 0 : -14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: reduceMotion ? 0 : -8,
                transition: { duration: reduceMotion ? 0.12 : 0.16 },
              }}
              transition={{
                duration: reduceMotion ? 0.12 : 0.24,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <Container size="xl">
                <div className="grid grid-cols-12 py-10 gap-x-10 gap-y-8">
                  {services.groups.map((group) => (
                    <div key={group.heading} className="col-span-3">
                      <p className="mb-4 eyebrow">{group.heading}</p>
                      <ul className="space-y-1">
                        {group.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              onClick={closeNow}
                              aria-current={currentPage(link.to)}
                              className="block px-3 py-2 -mx-3 transition-colors rounded-md group hover:bg-surface-sunken"
                            >
                              <span className="block text-[0.9375rem] font-semibold text-primary group-hover:text-accent-text group-aria-[current=page]:text-accent-text transition-colors">
                                {link.label}
                              </span>
                              {link.description && (
                                <span className="mt-0.5 block text-[0.8125rem] leading-snug text-muted">
                                  {link.description}
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  <div className="col-span-3">
                    <p className="mb-4 eyebrow">{services.industries.heading}</p>

                    <ul className="space-y-0.5">
                      {services.industries.links.map((link) => (
                        <li key={link.to}>
                          <Link
                            to={link.to}
                            onClick={closeNow}
                            aria-current={currentPage(link.to)}
                            className="block rounded-md px-3 py-1.5 -mx-3 text-sm font-medium text-secondary transition-colors hover:bg-surface-sunken hover:text-primary aria-[current=page]:text-accent-text"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={services.industries.viewAll.to}
                      onClick={closeNow}
                      aria-current={currentPage(services.industries.viewAll.to)}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-text hover:underline"
                    >
                      {services.industries.viewAll.label}
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                        />
                      </svg>
                    </Link>

                    <div className="p-5 mt-6 border rounded-lg border-border bg-surface-sunken">
                      <p className="eyebrow">
                        {services.featured.eyebrow}
                      </p>
                      <Link
                        to={services.featured.cta.to}
                        onClick={closeNow}
                        className="w-full mt-3 btn btn-secondary"
                      >
                        {services.featured.cta.label}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>

                      <div className="mt-4 space-y-1">
                        {services.featured.secondary.map((secLink) => (
                          <Link
                            key={secLink.to}
                            to={secLink.to}
                            onClick={closeNow}
                            className="flex items-center gap-1.5 text-sm text-secondary transition-colors hover:text-accent-text"
                          >
                            <span>{secLink.label}</span>
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                              />
                            </svg>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Container>
            </m.div>
          </>
        )}
      </AnimatePresence>
    </li>
  );
};

export default ServicesMegaMenu;
